/**
 * Date utilities — single source of truth for date parsing in ea-app.
 *
 * IMPORTANT: `new Date("2025-03-15")` parses as UTC midnight, which shifts
 * to the previous day in US timezones.  Always use `parseLocalDate` instead
 * so every date string is treated as local midnight.
 */

/**
 * Parse a date string as local midnight, avoiding the UTC timezone shift.
 * Use this instead of `new Date(dateString)` for YYYY-MM-DD strings.
 * @param {string|Date} dateString - YYYY-MM-DD string, ISO timestamp, or Date object
 * @returns {Date} Date object at local midnight
 */
export function parseLocalDate(dateString) {
  if (dateString instanceof Date) return dateString;
  if (!dateString) return new Date(NaN);
  const str = String(dateString);
  // Strip any time/timezone component
  const dateOnly = str.includes('T') ? str.split('T')[0] : str;
  // YYYY-MM-DD → append T00:00:00 to force local-time parse
  // Other formats (e.g. "3/15/2026" from toLocaleString) → let the engine parse natively
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    return new Date(dateOnly + 'T00:00:00');
  }
  return new Date(dateOnly);
}

/**
 * Convert a Date object to YYYY-MM-DD string using local timezone.
 * @param {Date} date
 * @returns {string} YYYY-MM-DD
 */
export function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Get today's date as YYYY-MM-DD in local timezone.
 * @returns {string} YYYY-MM-DD
 */
export function todayDateStr() {
  return toDateStr(new Date());
}

/**
 * Coerce any date-like input (ISO string, "MM/DD/YYYY", Date) to YYYY-MM-DD.
 * String formats are parsed by pattern (not Date) to avoid timezone drift —
 * UTC ISO strings ("2026-06-15T00:00:00.000Z") and local US strings
 * ("6/15/2026") both yield the calendar date as written.
 * @param {string|Date|null|undefined} input
 * @returns {string} YYYY-MM-DD or ''
 */
export function toIsoDateStr(input) {
  if (!input) return '';
  if (typeof input === 'string') {
    const isoMatch = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    const usMatch = input.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (usMatch) {
      const m = String(usMatch[1]).padStart(2, '0');
      const d = String(usMatch[2]).padStart(2, '0');
      return `${usMatch[3]}-${m}-${d}`;
    }
  }
  const d = input instanceof Date ? input : new Date(input);
  if (isNaN(d.getTime())) return '';
  return toDateStr(d);
}

/**
 * Format a date string to a short, readable format (e.g., "Jan 15, 2024")
 * @param {string} dateString - ISO date string or date-like string
 * @returns {string|null} Formatted date string, or null if invalid
 */
export function formatDateShort(dateString) {
  if (!dateString) return null;

  const date = parseLocalDate(dateString);

  if (isNaN(date.getTime())) return null;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Format a date string to mm/dd/yyyy format
 * @param {string} dateString - ISO date string or date-like string
 * @returns {string} Formatted date string as mm/dd/yyyy, or 'N/A' if invalid
 */
export function formatDateNumeric(dateString) {
  if (!dateString) return 'N/A';

  try {
    const date = parseLocalDate(dateString);
    if (isNaN(date.getTime())) return 'N/A';

    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    return 'N/A';
  }
}

/**
 * Returns a Date `months` calendar months after the given date, parsed as local
 * midnight via parseLocalDate (avoiding the UTC shift), or `null` when the input
 * is missing or unparseable. Does not mutate a Date passed in.
 * @param {string|Date|null|undefined} dateString
 * @param {number} months
 * @returns {Date|null}
 */
export function addMonths(dateString, months) {
  const time = parseLocalDate(dateString).getTime();
  if (isNaN(time)) return null;

  const result = new Date(time);
  result.setMonth(result.getMonth() + months);
  return result;
}

// Youngest age a learner can register or save a profile with (ULC registration floor).
export const MIN_LEARNER_AGE = 5;

/**
 * Whole calendar years between a birth date and `today` (birthday counts on the day).
 * @param {string|Date} birthDate - YYYY-MM-DD, ISO timestamp, or Date
 * @param {Date} [today]
 * @returns {number} age in years, or NaN when birthDate is missing/unparseable
 */
export function ageInYears(birthDate, today = new Date()) {
  const dob = parseLocalDate(birthDate);
  if (isNaN(dob.getTime())) return NaN;

  let age = today.getFullYear() - dob.getFullYear();
  const birthdayPending =
    today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
  return birthdayPending ? age - 1 : age;
}

/**
 * Latest birth date (YYYY-MM-DD) that is at least `years` old on `today`.
 * Feb 29 clamps to Feb 28 in non-leap years so the picker never allows a date ageInYears rejects.
 * @param {number} years
 * @param {Date} [today]
 * @returns {string}
 */
export function latestBirthDateForAge(years, today = new Date()) {
  const date = new Date(today.getFullYear() - years, today.getMonth(), today.getDate());
  if (date.getMonth() !== today.getMonth()) date.setDate(0);
  return toDateStr(date);
}
